import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';
import { message, Modal } from 'antd';

import { setEditingChapterId, updateGiide } from '../actions';
import { PublishGiideMutation, PublishState } from '../graphql/api';
import { Giide, IState } from '../graphql/types';
import useUser from './useUser';
import GiidePreview from '../components/GiideWithStep/GiidePreview';
import { publishGiide } from '../graphql/mutations';

const usePreview = () => {
  const dispatch = useDispatch();
  const giide = useSelector<IState, Giide | undefined>(state => state.giide);

  const { user } = useUser();

  const [previewLoading, setPreviewLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const loadPreview = useCallback(
    async (displayError: boolean = true, displayModal: boolean = false) => {
      let loadSuccessful = false;
      if (giide?.id) {
        try {
          setPreviewLoading(true);
          const prevState = giide?.publishState;

          dispatch(
            updateGiide(
              { publishState: PublishState.migrating } as any as Giide,
              true
            )
          );

          const result = await (API.graphql(
            graphqlOperation(publishGiide, {
              input: {
                giideId: giide.id,
                userId: user?.userId,
                isPreview: true,
              },
            })
          ) as Promise<{ data: PublishGiideMutation }>);

          if (result.data.publishGiide?.result === 'ok') {
            dispatch(
              updateGiide({ publishState: prevState } as any as Giide, true)
            );
          } else {
            throw new Error(
              'Error occured while preparing your Giide for preview.'
            );
          }

          dispatch(setEditingChapterId(undefined));
          loadSuccessful = true;
          if (displayModal) openModal();
        } catch (err) {
          if (displayError) {
            message.error(
              'Error occured while preparing your Giide for preview.'
            );
          }
          dispatch(
            updateGiide(
              { publishState: PublishState.error } as any as Giide,
              true
            )
          );
        } finally {
          setPreviewLoading(false);
        }
      }
      return loadSuccessful;
    },
    [giide, dispatch, user, openModal]
  );

  const previewModal = useCallback(() => {
    return (
      giide &&
      user?.tenantID &&
      user.userId && (
        <Modal
          open={modalOpen}
          closable={true}
          onCancel={closeModal}
          width={480}
          footer={null}
          destroyOnClose={true}
        >
          <GiidePreview giideId={giide.id} closeModal={closeModal} />
        </Modal>
      )
    );
  }, [giide, closeModal, user, modalOpen]);

  return { loadPreview, previewLoading, previewModal };
};

export default usePreview;
